import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { ModuleRow } from "@/mobile/ui";
import { moduleHref } from "@/mobile/open";

// Short forms people type → the module that holds those records.
const ALIASES = {
  wo: "/work-orders", "work order": "/work-orders", po: "/purchase-orders", pr: "/purchase-requests", dc: "/delivery-challans",
  challan: "/delivery-challans", gin: "/goods-inward", grn: "/goods-receipt", part: "/items", item: "/items", bom: "/items",
  dealer: "/dealer-master", customer: "/customers", supplier: "/supplier-master", vendor: "/supplier-master", employee: "/hr",
  ncr: "/quality/production-ncrs", ecn: "/design", ecr: "/design", drawing: "/design", invoice: "/tax-invoices", lead: "/crm",
  so: "/customer-sales-orders", to: "/sales-orders", quote: "/quotations", stock: "/stock-availability",
};

// Searches the modules and sub-pages this user can open. Records (a WO, PO,
// DC, part…) are searched inside each module's own page.
export default function MobileSearch({ groups, query, section }) {
  const nav = useNavigate();
  const q = query.trim().toLowerCase();
  const scope = section && section !== "all" ? groups.filter((g) => g.key === section) : groups;
  const entries = scope.flatMap((g) => g.modules.flatMap((m) => [
    { m, href: moduleHref(m), label: m.label, sub: g.label, hay: `${m.label} ${g.label}` },
    ...(m.children || []).filter((c) => c.to !== m.to).map((c) => ({ m: { ...c, icon: m.icon }, href: c.to, label: c.label, sub: `${g.label} › ${m.label}`, hay: `${c.label} ${m.label}` })),
  ]));
  const aliasTo = Object.entries(ALIASES).filter(([k]) => q && (q === k || q.startsWith(k + " "))).map(([, v]) => v);
  const results = !q ? [] : entries.filter((e) => e.hay.toLowerCase().includes(q) || aliasTo.includes(e.m.to));
  return (
    <div className="pb-6" data-testid="mobile-search">
      {!q && (
        <div className="p-6 text-center text-[var(--m-muted)]">
          <Search className="w-8 h-8 mx-auto opacity-60" />
          <div className="mt-2 text-[15px] font-semibold text-[var(--m-text)]">Search the ERP</div>
          <div className="text-[13px] mt-1">Type a module or page name — e.g. “GRN”, “Assembly”, “Tax Invoices”.</div>
        </div>
      )}
      {q && (
        <>
          <div className="px-3 pt-3 pb-1.5 text-[12px] font-bold uppercase tracking-wide text-[var(--m-muted)]" data-testid="mobile-search-count">
            {results.length} {results.length === 1 ? "match" : "matches"} for “{query}”
          </div>
          {results.length > 0 && (
            <div className="divide-y divide-[var(--m-border)] border-y border-[var(--m-border)]" data-testid="mobile-search-results">
              {results.map((r) => <ModuleRow key={r.href} module={{ ...r.m, label: r.label }} sub={r.sub} onClick={() => nav(r.href)} />)}
            </div>
          )}
          {results.length === 0 && <div className="px-3 py-6 text-center text-[14px] text-[var(--m-muted)]">No modules or pages match.</div>}
          <p className="px-3 pt-3 text-[12px] text-[var(--m-muted)]">
            Looking for a specific record (a WO, PO, DC, part, dealer, customer or employee)? Open its module and use the search on that page.
          </p>
        </>
      )}
    </div>
  );
}
